import { defineStore } from 'pinia';
import { markRaw } from 'vue';
import { getDocs, query, collection, where, limit } from 'firebase/firestore';
import {useFirestore} from 'vuefire'
import {useWebsiteConfigurationStore} from '~/stores/websiteConfigurationStore';

export const useListingStore = defineStore('listing', {
  state: () => ({
    listings: {}, // Use a plain object to store listings data
    lastFetchTimes: {} // Store fetch timestamps for each slug
  }),
  actions: {
    // Batch-loads listing documents (project or property slugs) for a page of
    // Algolia hits with a single `where in` query (chunked to stay inside
    // Firestore's 30-disjunction limit). Results land in `listings` keyed by
    // slug; cached slugs are skipped so re-renders of the same results page
    // cost no reads.
    async fetchListingsBySlugs(slugs) {
      const cacheDuration = 60 * 1000;
      const now = Date.now();
      const missingSlugs = [...new Set(slugs)].filter((slug) => {
        return slug && !(this.lastFetchTimes[slug] && now - this.lastFetchTimes[slug] < cacheDuration);
      });
      if (missingSlugs.length === 0) return;

      // Stamp up-front so concurrent calls don't refetch the same slugs;
      // slugs that return no document also stay quiet for the cache window.
      missingSlugs.forEach((slug) => { this.lastFetchTimes[slug] = now; });

      const db = await useFirestore();
      const { listingId, languageCode } = useWebsiteConfigurationStore().configuration;

      const chunks = [];
      for (let i = 0; i < missingSlugs.length; i += 30) {
        chunks.push(missingSlugs.slice(i, i + 30));
      }

      await Promise.all(chunks.map(async (chunk) => {
        try {
          const querySnapshot = await getDocs(
            query(
              collection(db, 'services/mls/listings'),
              where("listing_id", "==", listingId),
              where("language_code", "==", languageCode),
              where("slug", "in", chunk),
              limit(chunk.length)
            )
          );
          querySnapshot.forEach(doc => {
            // markRaw: these documents are large (full announce.assets arrays)
            // and immutable once fetched — deep-proxying them makes every
            // card computed walk thousands of reactive getters and freezes
            // the page when a whole results page lands at once. Key-level
            // reactivity on `listings` is enough for cards to update.
            const plainListing = markRaw(JSON.parse(JSON.stringify(doc.data())));
            // Some documents lack announce.assets; normalize so consumers can
            // iterate without guarding.
            plainListing.announce ??= {};
            plainListing.announce.assets ??= [];
            this.listings[plainListing.slug] = plainListing;
          });
          // Mark slugs without a document as settled (null) so cards can
          // stop showing loading skeletons for them.
          chunk.forEach((slug) => {
            if (this.listings[slug] === undefined) {
              this.listings[slug] = null;
            }
          });
        } catch (error) {
          console.error('[listingStore] failed to fetch project listings', { chunk, error });
        }
      }));
    },
    async fetchListing(slug) {
      var db = await useFirestore();
      const cacheDuration = 60 * 1000; // 60 seconds
      const now = Date.now();

      // Extract project and property slugs
      const [projectSlug, propertySlug] = slug.split('/');

      // Check cache validity for both project and property
      const isPropertyCached = this.lastFetchTimes[slug] && now - this.lastFetchTimes[slug] < cacheDuration;
      const isProjectCached = this.lastFetchTimes[projectSlug] && now - this.lastFetchTimes[projectSlug] < cacheDuration;
      if (isPropertyCached && isProjectCached) {
        // Return cached data if available
        let assets = this.listings[projectSlug]?.announce?.assets ? toRaw(this.listings[projectSlug].announce.assets) : [];
        assets = [...assets];

        if (slug !== projectSlug) {
          let propertyAssets = toRaw(this.listings[slug]?.announce?.assets ?? []);
          assets.push(...propertyAssets);
        }
        return {
          data: {
            propertyListing: this.listings[slug],
            projectListing: this.listings[projectSlug],
            assets: assets
          }
        };
      }

      // Fetch new data
      const { listingId, languageCode } = useWebsiteConfigurationStore().configuration;
      const querySnapshot = await getDocs(
        query(
          collection(db, 'services/mls/listings'),
          where("listing_id", "==", listingId),
          where("language_code", "==", languageCode),
          where("slug", "in", [projectSlug, slug]), // Fetch both project and property listings
          limit(2)
        )
      );

      if (querySnapshot.docs.length > 0) {
        var result = { data: { assets: [] } };
        await querySnapshot.forEach(doc => {
          var listing = doc.data();

          // Convert the Firestore document to a plain JavaScript object.
          // markRaw: immutable and large — see fetchListingsBySlugs.
          var plainListing = markRaw(JSON.parse(JSON.stringify(listing)));
          // Some documents lack announce.assets; normalize so consumers can
          // iterate without guarding.
          plainListing.announce ??= {};
          plainListing.announce.assets ??= [];
          if (listing.slug === projectSlug) { // Project listing
            this.listings[projectSlug] = plainListing;
            this.lastFetchTimes[projectSlug] = now;
            result.data.projectListing = plainListing;
            result.data.assets.push(...plainListing.announce.assets);
          } else if (listing.slug === slug) { // Property listing (only if different from project)
            this.listings[slug] = plainListing;
            this.lastFetchTimes[slug] = now;
            result.data.propertyListing = plainListing;
            result.data.assets.push(...plainListing.announce.assets);
          }
        });

        if (!result.data.projectListing) {
          console.error('[listingStore] query matched docs but no project listing', {
            slug, projectSlug, listingId, languageCode,
            matchedSlugs: querySnapshot.docs.map(doc => doc.data().slug)
          });
        }

        return result;
      } else {
        console.error('[listingStore] no listings found', { slug, projectSlug, listingId, languageCode });
        return { data: null };
      }
    }
  }
});
